import { Link, useCanGoBack, useRouter } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import {
  Archive,
  ArchiveRestore,
  Calendar,
  ChevronDown,
  CircleArrowLeft,
  Clock,
  CloudUpload,
  Columns3,
  Copy,
  Loader2,
  Menu,
  Pencil,
  Trash2,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useGetClass } from '~entities/classes/api/get-class';
import { useGetStudentsByClassId } from '~entities/classes/api/get-enrolled-students-by-class-id';
import { useUpdateClassArchiveStatus } from '~entities/classes/api/update-class-archive-status';
import { type ClassStudent } from '~entities/student/modal/class-student';
import type { StudentSearch } from '~entities/student/modal/student-search';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { AgeRangeWarningDialog } from '~features/classes/ui/age-range-warning-dialog';
import { BookingLinkDialog } from '~features/classes/ui/booking-link-dialog';
import { CopyClassButton } from '~features/classes/ui/copy-class-button';
import { EnrollStudentModal } from '~features/students/ui/student-payment/enroll-students';
import { StudentSelector } from '~features/students/ui/student-selector';

import { AttendanceExportDropdown } from '~shared/components/attendance-export-dropdown';
import { DropClassDialog } from '~shared/components/dialog/drop-class-dialog';
import { EnrollioTable } from '~shared/components/enrollio-table';
import { MedicalBadgeWithTooltip } from '~shared/components/medical-details';
import { DataTableColumnHeader } from '~shared/components/table/header';
import {
  ENROLLED_STUDENTS_CSV_HEADERS,
  exportToCSV,
} from '~shared/helpers/csv';
import {
  calculateAgeFromISO,
  formatAgeRange,
  formatAgeString,
  formatDateOnlySafe,
  parseToNoonDate,
} from '~shared/helpers/datetime';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~shared/shadcn/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';
import { isAgeInRange } from '~shared/utils/date-time';

import { EditBookingLink } from './edit-booking-link';

// Create a column helper
const columnHelper = createColumnHelper<ClassStudent>();
const ROW_NUMBER_HEADER = '#';

const classEnrollmentStatus = (
  student: ClassStudent,
  classId: string,
): string | undefined => {
  const match = student.enrollments?.find((enrollment) => {
    const id =
      typeof enrollment.enrollmentId === 'string'
        ? enrollment.enrollmentId
        : (enrollment.enrollmentId as { _id?: string } | undefined)?._id;
    return id === classId;
  });
  return match?.subscriptionStatus;
};

const createColumns = (classId: string) => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => {
          table.toggleAllPageRowsSelected(!!value);
        }}
        aria-label='Select all'
      />
    ),
    cell: ({ row }) => {
      return (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => {
            row.toggleSelected(!!value);
          }}
          aria-label='Select row'
        />
      );
    },
  }),
  columnHelper.display({
    id: 'rowNumber',
    header: () => <div className='text-black'>#</div>,
    cell: ({ row, table }) => {
      const { pageIndex, pageSize } = table.getState().pagination;
      const rowIndex = table.getRowModel().rows.indexOf(row);
      const rowNumber = pageIndex * pageSize + rowIndex + 1;
      return rowNumber;
    },
  }),
  columnHelper.accessor('name', {
    header: (info) => (
      <DataTableColumnHeader
        className='text-black'
        column={info.column}
        title='Student Name'
      />
    ),
    cell: (info) => (
      <div className='flex items-center gap-2'>
        <Button variant={'link'} className='text-custom-secondary-1' asChild>
          <Link
            to={`/students/$studentId/details`}
            params={{
              studentId: info.row.original._id,
            }}
          >
            {info.getValue()}
          </Link>
        </Button>
        <MedicalBadgeWithTooltip medical={info.row.original.medical} />
      </div>
    ),
  }),
  columnHelper.accessor(
    (row) => classEnrollmentStatus(row, classId) ?? 'active',
    {
      id: 'status',
      meta: { mobileLabel: 'Status' },
      header: (info) => (
        <DataTableColumnHeader
          className='pl-4 text-black'
          column={info.column}
          title='Status'
        />
      ),
      cell: (info) => {
        const status = info.getValue();
        const isInactive =
          status === 'term_ended' ||
          status === 'ended' ||
          status === 'inactive' ||
          status === 'dropped' ||
          status === 'cancelled';
        return (
          <div className='px-4'>
            {isInactive ? (
              <Badge variant='secondary' className='text-xs'>
                Inactive
              </Badge>
            ) : (
              <Badge variant='default' className='text-xs'>
                Active
              </Badge>
            )}
          </div>
        );
      },
    },
  ),
  columnHelper.accessor('gender', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Gender'
      />
    ),
    cell: (info) => <div className='px-4'> {info.getValue()}</div>,
  }),
  columnHelper.accessor('dob', {
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='DOB'
      />
    ),
    cell: (info) => {
      const dob = info.getValue();
      if (!dob) return <div className='px-4'>-</div>;
      return (
        <div className='px-4'>
          {formatDateOnlySafe(dob, 'MM-dd-yyyy', 'Invalid date')}
        </div>
      );
    },
  }),
  columnHelper.accessor('dob', {
    id: 'age',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Age'
      />
    ),
    cell: (info) => {
      const dob = info.getValue();
      if (!dob) {
        return <div className='px-4'>—</div>;
      }
      const { years, months } = calculateAgeFromISO(dob);
      return (
        <div className='px-4'>
          {`${years} ${years > 1 ? 'years' : 'year'} ${months} ${months > 1 ? 'months' : 'month'}`}
        </div>
      );
    },
  }),
  columnHelper.accessor('enrollments', {
    id: 'enrollmentDate',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Enrollment Date'
      />
    ),
    cell: (info) => {
      const enrollments = info.getValue();
      const enrollment = enrollments.find((e) => e.enrollmentId === classId);
      if (!enrollment?.enrolledDate) {
        return <div className='px-4'>-</div>;
      }
      const enrolledDate = parseToNoonDate(enrollment.enrolledDate);
      if (!enrolledDate) {
        return <div className='px-4'>-</div>;
      }
      return <div className='px-4'>{format(enrolledDate, 'MMMM d, yyyy')}</div>;
    },
  }),
  columnHelper.accessor('tShirtSize', {
    id: 'costumeSize',
    header: (info) => (
      <DataTableColumnHeader
        className='pl-4 text-black'
        column={info.column}
        title='Costume Size'
      />
    ),
    cell: (info) => <div className='px-4'> {info.getValue()}</div>,
  }),
];

interface ClassEnrollmentsPageProps {
  classId: string;
}

export const ClassEnrollmentsPage = ({
  classId,
}: ClassEnrollmentsPageProps) => {
  const { data: classData, isFetching: isFetchingClassData } = useGetClass({
    classId,
  });

  const router = useRouter();
  const canGoBack = useCanGoBack();
  const { data, isFetching } = useGetStudentsByClassId({ classId });
  const { hasAdminAccess, hasStaffAccess } = useCurrentUserRole();
  const archiveMutation = useUpdateClassArchiveStatus();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [showDropDialog, setShowDropDialog] = useState(false);
  const [classActionsOpen, setClassActionsOpen] = useState(false);
  const [classActionsMenuOpen, setClassActionsMenuOpen] = useState(false);
  const [copyClassDialogOpen, setCopyClassDialogOpen] = useState(false);
  const [enrollmentStudent, setEnrollmentStudent] = useState<StudentSearch>();
  const [enrollStudentDialogOpen, setEnrollStudentDialogOpen] = useState(false);

  const cols = useMemo(() => createColumns(classId), [classId]);

  const handleArchiveToggle = async () => {
    if (!classData?._id) return;

    try {
      await archiveMutation.mutateAsync({
        id: classData._id,
        isArchive: !classData.isArchive,
      });
      toast.success(
        classData.isArchive
          ? 'Class unarchived successfully'
          : 'Class archived successfully',
      );
    } catch {
      toast.error(
        classData.isArchive
          ? 'Failed to unarchive class'
          : 'Failed to archive class',
      );
    }
  };
  const [bookingLinkDialogOpen, setBookingLinkDialogOpen] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [editBookingLinkDialogOpen, setEditBookingLinkDialogOpen] =
    useState(false);
  const [ageRangeWarningOpen, setAgeRangeWarningOpen] = useState(false);
  const [pendingStudent, setPendingStudent] = useState<StudentSearch | null>(
    null,
  );
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');

  const isClassFull = classData != null && classData.remainingSeats <= 0;
  const canOverrideClassCapacity = hasAdminAccess || hasStaffAccess;
  const isEnrollmentBlockedByCapacity =
    isClassFull && !canOverrideClassCapacity;
  const classFullTooltip =
    'Class is full. You can increase class capacity to enroll more students.';

  const handleDropClass = () => {
    // Get the selected student ID from rowSelection
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length <= 0 || !data) return;

    const index = parseInt(selectedIds[0] ?? '0');
    const selectedStudentId = data[index]?._id;
    if (!selectedStudentId || !data[0]?.studioId) return;
    setSelectedStudentId(selectedStudentId);
    setShowDropDialog(true);
  };

  // Handle student selection for enrollment
  const onStudentSelectionChange = (
    value: StudentSearch | undefined | null,
  ) => {
    if (!value || !classData) return;

    // Calculate student age
    const studentAge = calculateAgeFromISO(value.dob);

    // Check if student is in age range
    const isInRange = isAgeInRange({
      age: {
        years: studentAge.years,
        months: studentAge.months,
      },
      startAge: {
        years: classData.startYear,
        months: classData.startMonth,
      },
      endAge: {
        years: classData.endYear,
        months: classData.endMonth,
      },
    });

    if (!isInRange) {
      // Show warning dialog
      setPendingStudent(value);
      setAgeRangeWarningOpen(true);
    } else {
      // Proceed directly with enrollment
      setEnrollStudentDialogOpen(true);
      setEnrollmentStudent(value);
    }
  };

  // Handle age range warning confirmation
  const handleAgeRangeWarningConfirm = () => {
    if (pendingStudent) {
      setEnrollStudentDialogOpen(true);
      setEnrollmentStudent(pendingStudent);
      setPendingStudent(null);
    }
    setAgeRangeWarningOpen(false);
  };

  // Handle age range warning dialog close
  const handleAgeRangeWarningClose = (open: boolean) => {
    setAgeRangeWarningOpen(open);
    if (!open) {
      setPendingStudent(null);
    }
  };

  const toggleEditBookingLinkDialog = () => {
    setBookingLinkDialogOpen(false);
    setEditBookingLinkDialogOpen(true);
  };

  const handleExport = () => {
    if (!data || data.length === 0) {
      console.error('No data available to export.');
      return;
    }

    // Class exports don't include Family Name
    const headers = [
      ROW_NUMBER_HEADER,
      ENROLLED_STUDENTS_CSV_HEADERS.STUDENT_NAME,
      ENROLLED_STUDENTS_CSV_HEADERS.GENDER,
      ENROLLED_STUDENTS_CSV_HEADERS.DOB,
      ENROLLED_STUDENTS_CSV_HEADERS.AGE,
      ENROLLED_STUDENTS_CSV_HEADERS.COSTUME_SIZE,
      ENROLLED_STUDENTS_CSV_HEADERS.ENROLLMENT_DATE,
    ];

    const formattedData = data.map((student, index) => {
      const { years, months } = calculateAgeFromISO(student.dob ?? '');
      const formattedDate = student.dob
        ? formatDateOnlySafe(student.dob, 'MM-dd-yyyy', '')
        : '';
      const enrolledDate = student.enrollments.find(
        (e) => e.enrollmentId === classId,
      )?.enrolledDate;
      const formattedEnrolledDate = enrolledDate
        ? parseToNoonDate(enrolledDate)
        : null;
      const formattedEnrolledDateString = formattedEnrolledDate
        ? format(formattedEnrolledDate, 'MMMM d, yyyy')
        : null;

      return {
        [ROW_NUMBER_HEADER]: index + 1,
        [ENROLLED_STUDENTS_CSV_HEADERS.STUDENT_NAME]: student.name,
        [ENROLLED_STUDENTS_CSV_HEADERS.GENDER]: student.gender,
        [ENROLLED_STUDENTS_CSV_HEADERS.DOB]: formattedDate,
        [ENROLLED_STUDENTS_CSV_HEADERS.AGE]: formatAgeString(years, months),
        [ENROLLED_STUDENTS_CSV_HEADERS.COSTUME_SIZE]: student.tShirtSize || '',
        [ENROLLED_STUDENTS_CSV_HEADERS.ENROLLMENT_DATE]:
          formattedEnrolledDateString,
      };
    });

    const className = classData?.title ?? 'class';
    exportToCSV(
      formattedData,
      headers,
      `${className}-enrolled-students-${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.csv`,
    );
  };

  const closeClassActions = () => {
    setClassActionsOpen(false);
    setClassActionsMenuOpen(false);
  };

  const renderBulkEnrollButton = (stacked: boolean) => {
    if (!hasAdminAccess) return null;
    const buttonClassName = stacked ? 'w-full justify-start' : undefined;

    if (isEnrollmentBlockedByCapacity) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={stacked ? 'block w-full' : 'inline-flex'}>
              <Button variant='secondary' className={buttonClassName} disabled>
                <Users />
                Bulk Enroll Students
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{classFullTooltip}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Button variant='secondary' className={buttonClassName} asChild>
        <Link
          to={'/classes/$classId/bulk-enroll-students'}
          params={{ classId }}
          onClick={closeClassActions}
        >
          <Users />
          Bulk Enroll Students
        </Link>
      </Button>
    );
  };

  const renderClassManagementActions = (stacked: boolean) => {
    const buttonClassName = stacked ? 'w-full justify-start' : undefined;

    return (
      <>
        {hasAdminAccess && (
          <Button
            variant='outline'
            className={buttonClassName}
            onClick={() => {
              closeClassActions();
              if (classData?._id) {
                setSelectedClassId(classData._id);
                setBookingLinkDialogOpen(true);
              }
            }}
          >
            <Calendar />
            Book Trial Class
          </Button>
        )}

        {hasAdminAccess && !classData?.isArchive && classData != null && (
          <Button
            variant='outline'
            size={stacked ? 'default' : 'sm'}
            className={buttonClassName}
            onClick={() => {
              closeClassActions();
              setCopyClassDialogOpen(true);
            }}
          >
            <Copy />
            Copy Class
          </Button>
        )}

        {hasAdminAccess && (
          <Button
            variant='outline'
            size={stacked ? 'default' : 'sm'}
            className={buttonClassName}
            onClick={() => {
              closeClassActions();
              void handleArchiveToggle();
            }}
            disabled={archiveMutation.isPending}
          >
            {archiveMutation.isPending ? (
              <Loader2 className='animate-spin' />
            ) : classData?.isArchive ? (
              <ArchiveRestore />
            ) : (
              <Archive />
            )}
            {classData?.isArchive ? 'Unarchive Class' : 'Archive Class'}
          </Button>
        )}

        <Button
          variant='outline'
          className={buttonClassName}
          onClick={() => {
            closeClassActions();
            handleExport();
          }}
          disabled={!data || data.length === 0}
        >
          <CloudUpload />
          Exports
        </Button>

        {hasAdminAccess && (
          <div
            className={
              stacked
                ? 'w-full [&_button>svg:last-child]:ml-auto [&_button]:w-full [&_button]:justify-start'
                : undefined
            }
          >
            <AttendanceExportDropdown
              entityId={classId}
              entityTitle={classData?.title}
            />
          </div>
        )}

        <Button variant='outline' className={buttonClassName} asChild>
          <Link
            to={'/classes/$classId/wait-list'}
            params={{ classId }}
            onClick={closeClassActions}
          >
            <Clock />
            Waitlist
          </Link>
        </Button>

        {hasAdminAccess && (
          <Button variant='outline' className={buttonClassName} asChild>
            <Link
              to={'/classes/$classId/update'}
              params={{ classId }}
              onClick={closeClassActions}
            >
              <Pencil />
              {stacked ? 'Edit' : null}
            </Link>
          </Button>
        )}
      </>
    );
  };

  return (
    <div>
      {(isFetching || isFetchingClassData) && (
        <Loader2 className='mx-auto size-8 animate-spin' />
      )}
      <div className='mb-6 flex items-start gap-2'>
        {canGoBack ? (
          <Button
            variant={'ghost'}
            className='mt-0.5 flex items-center p-2 [&_svg]:size-6'
            disabled={!canGoBack}
            onClick={() => {
              router.history.back();
            }}
          >
            <CircleArrowLeft className='size-6' />
          </Button>
        ) : (
          <Button
            variant={'ghost'}
            className='mt-0.5 flex items-center p-2 [&_svg]:size-6'
            asChild
          >
            <Link to='/classes'>
              <CircleArrowLeft className='size-6' />
            </Link>
          </Button>
        )}
        <div className='min-w-0 flex-1'>
          <h2 className='break-words text-xl font-semibold sm:text-2xl'>
            {classData?.title}
          </h2>
          {isFetchingClassData ? (
            <Loader2 className='mt-2 animate-spin' />
          ) : (
            <Badge className='mt-2 text-secondary'>
              Age Range:{' '}
              {formatAgeRange(
                classData?.startYear,
                classData?.startMonth,
                classData?.endYear,
                classData?.endMonth,
              )}
            </Badge>
          )}
        </div>
      </div>

      <div className='mb-10 mt-6'>
        <EnrollioTable
          ariaLabel='Students enrolled in class'
          totalLabel='Enrolled Students'
          columnFilters={columnFilters}
          columnVisibility={columnVisibility}
          columns={cols}
          data={data ?? []}
          enableRowSelection={true}
          onColumnFiltersChange={setColumnFilters}
          onColumnVisibilityChange={setColumnVisibility}
          onRowSelectionChange={setRowSelection}
          onSortingChange={setSorting}
          rowSelection={rowSelection}
          sorting={sorting}
          showSelectionCount
          filtersLayout='inline'
          filterHeader={(table) => (
            <Input
              placeholder='Search Students...'
              onChange={(event) =>
                table.getColumn('email')?.setFilterValue(event.target.value)
              }
              className='w-full max-w-sm'
            />
          )}
          toolbar={(table) => {
            const renderColumnsMenu = (stacked: boolean) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant='outline'
                    className={stacked ? 'w-full justify-start' : undefined}
                  >
                    <Columns3 />
                    Columns
                    <ChevronDown
                      className={stacked ? 'ml-auto size-4' : 'ml-2 size-4'}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className='capitalize'
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => {
                          column.toggleVisibility(!!value);
                        }}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );

            const renderStudentSelector = (fullWidth: boolean) =>
              hasAdminAccess ? (
                isEnrollmentBlockedByCapacity ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={
                          fullWidth ? 'block w-full min-w-0' : 'inline-flex'
                        }
                      >
                        <StudentSelector
                          onChange={onStudentSelectionChange}
                          isDisabled
                          className={fullWidth ? 'w-full' : undefined}
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{classFullTooltip}</TooltipContent>
                  </Tooltip>
                ) : (
                  <StudentSelector
                    onChange={onStudentSelectionChange}
                    className={fullWidth ? 'w-full' : undefined}
                  />
                )
              ) : null;

            const renderDropClassButton = () =>
              Object.keys(rowSelection).length === 1 ? (
                <Button
                  variant='destructive'
                  onClick={() => {
                    handleDropClass();
                  }}
                >
                  <Trash2 className='mr-2 size-4' />
                  Drop Class
                </Button>
              ) : null;

            return (
              <div className='flex w-full min-w-0 flex-col items-stretch gap-4'>
                <div className='hidden w-full min-w-0 flex-wrap items-center gap-2 md:flex'>
                  {renderStudentSelector(false)}
                  {renderBulkEnrollButton(false)}
                  {renderDropClassButton()}
                  <Popover
                    open={classActionsMenuOpen}
                    onOpenChange={setClassActionsMenuOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button variant='outline'>
                        <Menu className='mr-2 size-4' />
                        Class actions
                        <ChevronDown className='ml-2 size-4' />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align='end' className='w-56 p-2'>
                      <div className='flex flex-col gap-1'>
                        {renderClassManagementActions(true)}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {renderColumnsMenu(false)}
                </div>

                <div className='flex w-full min-w-0 flex-col gap-3 md:hidden'>
                  <Sheet
                    open={classActionsOpen}
                    onOpenChange={setClassActionsOpen}
                  >
                    <SheetTrigger asChild>
                      <Button variant='outline' className='w-full'>
                        <Menu className='mr-2 size-4' />
                        Class actions
                      </Button>
                    </SheetTrigger>
                    <SheetContent
                      side='right'
                      className='w-[300px] overflow-y-auto'
                    >
                      <SheetHeader>
                        <SheetTitle>Class actions</SheetTitle>
                        <SheetDescription>
                          Book a trial, copy or archive this class, export
                          lists, choose columns, or open the waitlist.
                        </SheetDescription>
                      </SheetHeader>
                      <div className='mt-6 flex flex-col gap-3'>
                        {renderBulkEnrollButton(true)}
                        {renderClassManagementActions(true)}
                        {renderColumnsMenu(true)}
                      </div>
                    </SheetContent>
                  </Sheet>
                  {renderStudentSelector(true)}
                  {renderDropClassButton()}
                </div>
              </div>
            );
          }}
        />
      </div>

      <EnrollStudentModal
        dialogOpen={enrollStudentDialogOpen}
        setDialogOpen={setEnrollStudentDialogOpen}
        studentId={enrollmentStudent?._id.toString() ?? ''}
        parentId={enrollmentStudent?.parentId}
        preSelectedSession={
          classData?.session
            ? {
                label: classData.session.name,
                value: classData.session._id,
              }
            : null
        }
        preSelectedClass={
          classData
            ? {
                label: classData.title,
                value: classData._id,
              }
            : null
        }
      />

      {showDropDialog && selectedStudentId && (
        <DropClassDialog
          open={showDropDialog}
          onOpenChange={setShowDropDialog}
          classId={classData?._id ?? ''}
          studentId={selectedStudentId}
          studioId={data?.[0]?.studioId ?? ''}
          onSuccess={() => {
            setShowDropDialog(false);
            setSelectedStudentId('');
            setRowSelection({});
          }}
        />
      )}

      {hasAdminAccess && !classData?.isArchive && classData != null ? (
        <CopyClassButton
          classId={classData._id}
          open={copyClassDialogOpen}
          onOpenChange={setCopyClassDialogOpen}
        />
      ) : null}

      {bookingLinkDialogOpen && (
        <BookingLinkDialog
          open={bookingLinkDialogOpen}
          onOpenChange={setBookingLinkDialogOpen}
          classId={selectedClassId}
          onEdit={toggleEditBookingLinkDialog}
        />
      )}

      {editBookingLinkDialogOpen && (
        <EditBookingLink
          open={editBookingLinkDialogOpen}
          onOpenChange={setEditBookingLinkDialogOpen}
          initialValue={classData?.calendarId_ghl ?? ''}
          classId={selectedClassId}
        />
      )}

      {pendingStudent && classData && (
        <AgeRangeWarningDialog
          open={ageRangeWarningOpen}
          onOpenChange={handleAgeRangeWarningClose}
          onConfirm={handleAgeRangeWarningConfirm}
          studentName={pendingStudent.name}
          studentAge={calculateAgeFromISO(pendingStudent.dob)}
          classAgeRange={{
            startYear: classData.startYear,
            startMonth: classData.startMonth,
            endYear: classData.endYear,
            endMonth: classData.endMonth,
          }}
        />
      )}
    </div>
  );
};
